/**
 * Asset General Info Service
 * 
 * @swagger-tag AssetGeneralInfos
 * @description Controller for asset general info operations.
 */
import ApiService from './api';
import type { components } from '@/core/types/generated/api';
import { buildPageQuery, toPaginatedResult, type PaginatedResult, type PageRequest } from '@/shared/utils/pagination';

// Export types from generated API
export type AssetGeneralInfoDto = components['schemas']['AssetGeneralInfoDto'];
export type AssetGeneralInfoDtoPaginatedResponse = components['schemas']['AssetGeneralInfoDtoPaginatedResponse'];
export type BulkCreateAssetGeneralInfosRequest = components['schemas']['BulkCreateAssetGeneralInfosRequest'];
export type BulkUpdateAssetGeneralInfosRequest = components['schemas']['BulkUpdateAssetGeneralInfosRequest'];
export type BulkDeleteAssetGeneralInfosRequest = components['schemas']['BulkDeleteAssetGeneralInfosRequest'];
export type AssetGeneralInfoDtoBulkOperationResult = components['schemas']['AssetGeneralInfoDtoBulkOperationResult'];
export type GuidBulkOperationResult = components['schemas']['GuidBulkOperationResult'];
export type AssetSearchFilterMetadataDto = components['schemas']['AssetSearchFilterMetadataDto'];
export type AssetGeneralInfoIndicatorsDto = components['schemas']['AssetGeneralInfoIndicatorsDto'];
export type AssetGeneralInfoIndicatorsTrendItemDto = components['schemas']['AssetGeneralInfoIndicatorsTrendItemDto'];
export type CloneAssetGeneralInfoRequestDto = components['schemas']['CloneAssetGeneralInfoRequestDto'];
export type AssetAuditSummaryDto = components['schemas']['AssetAuditSummaryDto'];

// === Search Column Metadata Types ===
// Manually defined until API spec regeneration picks up the new DTOs

export interface SearchColumnDefinition {
  id: string
  label: string
  propertyType: string
}

export interface AssetSearchColumnMetadataDto {
  columns: SearchColumnDefinition[]
}

/** Extended search result type that includes dynamically extracted nested values */
export type AssetSearchResultDto = AssetGeneralInfoDto & {
  nestedValues?: Record<string, unknown>
}

// === Bulk Edit Types ===
export interface BulkEditFieldInfo {
  entityName: string;
  fieldPath: string;
  displayName: string;
  fieldType: string;
  inputType: 'text' | 'number' | 'boolean' | 'date' | 'reference';
  referenceEndpoint?: string;
  referenceDisplayField?: string;
  referenceValueField?: string;
  referenceBusinessIdField?: string;
  isNullable: boolean;
  groupName: string;
  groupIcon?: string;
  sortOrder: number;
}

export interface BulkEditMetadataDto {
  editableFields: BulkEditFieldInfo[];
}

export interface BulkEditFieldUpdate {
  fieldPath: string;
  value: unknown;
}

export interface BulkPatchAssetsRequest {
  assetIds?: string[];
  filterExpression?: string;
  fieldUpdates: BulkEditFieldUpdate[];
}

export interface BulkPatchWarningDto {
  assetId: string;
  assetBusinessId: string;
  warningMessage: string;
}

export interface BulkPatchAssetsResultDto {
  totalMatched: number;
  totalUpdated: number;
  totalFailed: number;
  totalWarnings: number;
  failures: BulkPatchFailureDto[];
  warnings: BulkPatchWarningDto[];
}

export interface BulkPatchFailureDto {
  assetId: string;
  assetBusinessId: string;
  errorMessage: string;
}

// === Bulk Operation Types (Soft-Delete & Restore) ===
export interface BulkAssetOperationRequest {
  assetIds?: string[];
  filterExpression?: string;
  excludeIds?: string[];
}

export interface BulkAssetOperationResultDto {
  totalMatched: number;
  totalProcessed: number;
  totalFailed: number;
  failures: BulkOperationFailureDto[];
}

export interface BulkOperationFailureDto {
  assetId: string;
  assetBusinessId: string;
  errorMessage: string;
}

// Query parameters for paginated asset general infos
export interface AssetGeneralInfosListParams extends PageRequest {
  assetId?: string;
  levelId?: string;
  parentId?: string;
  isLocked?: boolean;
}

// Query parameters for asset search
export interface AssetSearchParams extends PageRequest {
  filters?: string;
  sorts?: string;
  version?: string;
  // Comment search criteria (passed as comment.* query params)
  commentParams?: Record<string, string | number | boolean>;
}

export interface AssetIndicatorTrendParams {
  startDate?: string;
  endDate?: string;
  version?: string;
}

export interface AssetGeneralInfoRestoreParams {
  includeChildren?: boolean;
  replacementParentId?: string;
  version?: string;
}

export interface AssetGeneralInfoSoftDeleteParams {
  /** Optional replacement parent asset general info ID to re-parent direct children. */
  replacementParentId?: string;
  version?: string;
}

// Service object following the pattern used in other services
export const AssetGeneralInfosService = {
  /**
   * Gets all asset general infos without pagination.
   * GET /api/AssetGeneralInfos/all
   */
  async getAll(): Promise<AssetGeneralInfoDto[]> {
    const response = await ApiService.get<AssetGeneralInfoDto[]>('/AssetGeneralInfos/all');
    return response.data;
  },

  /**
   * Gets paginated, filtered, and sorted asset general infos.
   * GET /api/AssetGeneralInfos
   */
  async getPaged(params: AssetGeneralInfosListParams = {}): Promise<PaginatedResult<AssetGeneralInfoDto, AssetGeneralInfoDtoPaginatedResponse>> {
    const query = buildPageQuery(params);
    const response = await ApiService.get<AssetGeneralInfoDtoPaginatedResponse>('/AssetGeneralInfos', { params: query });
    return toPaginatedResult<AssetGeneralInfoDto, AssetGeneralInfoDto, AssetGeneralInfoDtoPaginatedResponse>(
      response.data, 
      (item) => item as AssetGeneralInfoDto
    );
  },

  /**
   * Creates a new asset general info.
   * POST /api/AssetGeneralInfos
   */
  async create(assetGeneralInfo: Partial<AssetGeneralInfoDto>): Promise<AssetGeneralInfoDto> {
    const response = await ApiService.post<AssetGeneralInfoDto>('/AssetGeneralInfos', assetGeneralInfo);
    return response.data;
  },

  /**
   * Gets an asset general info by its identifier.
   * GET /api/AssetGeneralInfos/{id}
   */
  async getById(id: string): Promise<AssetGeneralInfoDto> {
    const response = await ApiService.get<AssetGeneralInfoDto>(`/AssetGeneralInfos/${id}`);
    return response.data;
  },

  /**
   * Updates an existing asset general info.
   * PUT /api/AssetGeneralInfos/{id}
   */
  async update(id: string, assetGeneralInfo: Partial<AssetGeneralInfoDto>): Promise<AssetGeneralInfoDto> {
    const response = await ApiService.put<AssetGeneralInfoDto>(`/AssetGeneralInfos/${id}`, assetGeneralInfo);
    return response.data;
  },

  /**
   * Deletes an asset general info.
   * DELETE /api/AssetGeneralInfos/{id}
   */
  async delete(id: string): Promise<void> {
    await ApiService.delete(`/AssetGeneralInfos/${id}`);
  },

  /**
   * Gets an asset top level by its asset identifier.
   * GET /api/AssetGeneralInfos/by-asset/{assetId}
   */
  async getByAssetId(assetId: string): Promise<AssetGeneralInfoDto> {
    const response = await ApiService.get<AssetGeneralInfoDto>(`/AssetGeneralInfos/by-asset/${assetId}`);
    return response.data;
  },

  /**
   * Gets child assets for a specific parent asset.
   * GET /api/AssetGeneralInfos/children/{parentId}
   */
  async getChildAssets(parentId: string): Promise<AssetGeneralInfoDto[]> {
    const response = await ApiService.get<AssetGeneralInfoDto[]>(`/AssetGeneralInfos/children/${parentId}`);
    return response.data;
  },

  /**
   * Gets all assets at a specific hierarchy level.
   * GET /api/AssetGeneralInfos/by-level/{levelId}
   */
  async getAssetsByLevel(levelId: string): Promise<AssetGeneralInfoDto[]> {
    const response = await ApiService.get<AssetGeneralInfoDto[]>(`/AssetGeneralInfos/by-level/${levelId}`);
    return response.data;
  },

  /**
   * Bulk updates multiple asset general infos.
   * PUT /api/AssetGeneralInfos/bulk
   */
  async bulkUpdate(request: BulkUpdateAssetGeneralInfosRequest): Promise<AssetGeneralInfoDtoBulkOperationResult> {
    const response = await ApiService.put<AssetGeneralInfoDtoBulkOperationResult>('/AssetGeneralInfos/bulk', request);
    return response.data;
  },

  /**
   * Bulk creates multiple asset general infos.
   * POST /api/AssetGeneralInfos/bulk
   */
  async bulkCreate(request: BulkCreateAssetGeneralInfosRequest): Promise<AssetGeneralInfoDtoBulkOperationResult> {
    const response = await ApiService.post<AssetGeneralInfoDtoBulkOperationResult>('/AssetGeneralInfos/bulk', request);
    return response.data;
  },

  /**
   * Bulk deletes multiple asset general infos.
   * DELETE /api/AssetGeneralInfos/bulk
   */
  async bulkDelete(request: BulkDeleteAssetGeneralInfosRequest): Promise<GuidBulkOperationResult> {
    const response = await ApiService.delete<GuidBulkOperationResult>('/AssetGeneralInfos/bulk', {
      data: request
    });
    return response.data;
  },

  /**
   * Clones an existing asset general info (deep copy) with optional subtree cloning.
   * POST /api/AssetGeneralInfos/clone
   */
  async clone(request: CloneAssetGeneralInfoRequestDto, version?: string): Promise<AssetGeneralInfoDto> {
    const params = version ? { version } : undefined;
    const response = await ApiService.post<AssetGeneralInfoDto>('/AssetGeneralInfos/clone', request, { params });
    return response.data;
  },

  /**
   * Soft deletes an asset general info.
   * DELETE /api/AssetGeneralInfos/{id}/soft-delete
   *
   * Query params:
   * - replacementParentId?: string Optional replacement parent asset general info ID to re-parent direct children.
   * - version?: string
   */
  async softDelete(id: string, params: AssetGeneralInfoSoftDeleteParams = {}): Promise<void> {
    await ApiService.delete(`/AssetGeneralInfos/${id}/soft-delete`, { params });
  },

  /**
   * Restores a soft-deleted asset general info.
   * PATCH /api/AssetGeneralInfos/{id}/restore
   */
  async restore(id: string, params: AssetGeneralInfoRestoreParams = {}): Promise<AssetGeneralInfoDto> {
    const response = await ApiService.patch<AssetGeneralInfoDto>(`/AssetGeneralInfos/${id}/restore`, null, { params });
    return response.data;
  },

  /**
   * Searches assets and returns only their IDs based on filters with a single API call.
   * GET /api/AssetGeneralInfos/search
   * 
   * Supports advanced filtering with nested entity properties:
   * - Direct AssetGeneralInfo properties: assetid, description, levelid, parentid, notes, islocked, isactive
   * - Nested entity properties: details.assettype.typeid, details.manufacturer.description, exdata.gasgroup.optionid, etc.
   * - Hierarchy level property: level.position
   * 
   * Examples:
   * - Simple: filters=assetid@=*pump*,isactive==true
   * - Nested: filters=details.assettype.typeid==motor
   * - Cross-repository: filters=details.assettype.typeid==motor,exdata.isex==true,criticality.impact>=5
   */
  async searchAssets(params: AssetSearchParams = {}): Promise<PaginatedResult<AssetGeneralInfoDto, AssetGeneralInfoDtoPaginatedResponse>> {
    const { commentParams, ...rest } = params;
    const query = { ...buildPageQuery(rest), ...commentParams };
    const response = await ApiService.get<AssetGeneralInfoDtoPaginatedResponse>('/AssetGeneralInfos/search', { params: query });
    return toPaginatedResult<AssetGeneralInfoDto, AssetGeneralInfoDto, AssetGeneralInfoDtoPaginatedResponse>(
      response.data,
      (item) => item
    );
  },

  /**
   * Gets metadata about available search filters for assets.
   * GET /api/AssetGeneralInfos/search/filter-metadata
   * 
   * Provides information about which nested entities and properties support filtering.
   * Useful for building dynamic UI filter components.
   */
  async getSearchFilterMetadata(): Promise<AssetSearchFilterMetadataDto> {
    const response = await ApiService.get<AssetSearchFilterMetadataDto>('/AssetGeneralInfos/search/filter-metadata');
    return response.data;
  },

  /**
   * Gets available column definitions for asset search results.
   * GET /api/AssetGeneralInfos/search/column-metadata
   * 
   * Returns dynamically discovered column metadata from nested entities (Details, Criticalities).
   * Column IDs use dot-notation paths; labels use hierarchical " > " separators.
   */
  async getSearchColumnMetadata(): Promise<AssetSearchColumnMetadataDto> {
    const response = await ApiService.get<AssetSearchColumnMetadataDto>('/AssetGeneralInfos/search/column-metadata');
    return response.data;
  },

  /**
   * Gets summary indicators for asset general infos.
   * GET /api/AssetGeneralInfos/indicators
   */
  async getIndicators(version?: string): Promise<AssetGeneralInfoIndicatorsDto> {
    const params = version ? { version } : undefined;
    const response = await ApiService.get<AssetGeneralInfoIndicatorsDto>('/AssetGeneralInfos/indicators', { params });
    return response.data;
  },

  /**
   * Gets indicator trends over time.
   * GET /api/AssetGeneralInfos/indicators/trend
   */
  async getIndicatorsTrend(params: AssetIndicatorTrendParams = {}): Promise<AssetGeneralInfoIndicatorsTrendItemDto[]> {
    const response = await ApiService.get<AssetGeneralInfoIndicatorsTrendItemDto[]>('/AssetGeneralInfos/indicators/trend', { params });
    return response.data;
  },

  /**
   * Gets metadata about available fields for bulk editing.
   * GET /api/AssetGeneralInfos/bulk-edit/metadata
   */
  async getBulkEditMetadata(): Promise<BulkEditMetadataDto> {
    const response = await ApiService.get<BulkEditMetadataDto>('/AssetGeneralInfos/bulk-edit/metadata');
    return response.data;
  },

  /**
   * Bulk patches multiple assets with field-level updates.
   * PATCH /api/AssetGeneralInfos/bulk-edit
   */
  async bulkPatchAssets(request: BulkPatchAssetsRequest): Promise<BulkPatchAssetsResultDto> {
    const response = await ApiService.patch<BulkPatchAssetsResultDto>('/AssetGeneralInfos/bulk-edit', request);
    return response.data;
  },

  /**
   * Bulk soft-deletes multiple assets.
   * Supports both explicit asset ID selection and server-side filter-based selection.
   * Each asset is validated: fails if asset has active children, is already inactive, or is locked.
   * DELETE /api/AssetGeneralInfos/bulk-soft-delete
   */
  async bulkSoftDeleteAssets(request: BulkAssetOperationRequest): Promise<BulkAssetOperationResultDto> {
    const response = await ApiService.delete<BulkAssetOperationResultDto>('/AssetGeneralInfos/bulk-soft-delete', {
      data: request,
    });
    return response.data;
  },

  /**
   * Bulk restores multiple soft-deleted assets.
   * Supports both explicit asset ID selection and server-side filter-based selection.
   * Each asset is validated: fails if asset has an inactive parent or is already active.
   * PATCH /api/AssetGeneralInfos/bulk-restore
   */
  async bulkRestoreAssets(request: BulkAssetOperationRequest): Promise<BulkAssetOperationResultDto> {
    const response = await ApiService.patch<BulkAssetOperationResultDto>('/AssetGeneralInfos/bulk-restore', request);
    return response.data;
  },

  /**
   * Gets the aggregate audit summary for an asset — the most recent lastModifiedAt/By
   * across all related sub-objects (Details, Criticality, Documents, Ex Data, etc.).
   * GET /api/AssetGeneralInfos/{id}/audit-summary
   */
  async getAuditSummary(id: string): Promise<AssetAuditSummaryDto> {
    const response = await ApiService.get<AssetAuditSummaryDto>(`/AssetGeneralInfos/${id}/audit-summary`);
    return response.data;
  },
};
